<template>
  <a-drawer
    v-model:open="open"
    placement="right"
    :width="drawerWidth"
    :closable="false"
    :title="null"
    root-class-name="yt-notif-drawer-root"
    class="yt-notif-drawer"
    :body-style="{ padding: 0, height: '100%' }"
  >
    <div class="yt-notif">
      <div class="yt-notif__header">
        <div class="yt-notif__title-wrap">
          <span class="yt-notif__icon" aria-hidden="true">
            <i class="fa-solid fa-bell" />
          </span>
          <div>
            <h2 class="yt-notif__title">{{ $t('header.notifTitle') }}</h2>
            <p class="yt-notif__subtitle">{{ $t('header.notifSubtitle') }}</p>
          </div>
        </div>
        <button
          type="button"
          class="yt-notif__close"
          :aria-label="$t('header.close')"
          @click="close"
        >
          <i class="fa-solid fa-xmark" />
        </button>
      </div>

      <div v-if="!isLogin" class="yt-notif__login">
        <span class="yt-notif__login-icon" aria-hidden="true">
          <i class="fa-regular fa-bell-slash" />
        </span>
        <h3>{{ $t('header.notifLoginTitle') }}</h3>
        <p>{{ $t('header.notifLoginDesc') }}</p>
        <button type="button" class="yt-notif__login-btn" @click="goLogin">
          {{ $t('loginForm.login') }}
        </button>
      </div>

      <template v-else>
        <div class="yt-notif__tabs" role="tablist">
          <button
            v-for="tab in notifTabs"
            :key="tab.key"
            type="button"
            role="tab"
            class="yt-notif__tab"
            :class="{ 'is-active': notifFilter === tab.key }"
            :aria-selected="notifFilter === tab.key"
            @click="notifFilter = tab.key"
          >
            {{ tab.label }}
          </button>
        </div>

        <div class="yt-notif__list">
          <button
            v-for="item in filteredNotifications"
            :key="item.id"
            type="button"
            class="yt-notif__item"
            :class="{ 'is-unread': !item.read }"
            @click="markNotifRead(item)"
          >
            <span v-if="!item.read" class="yt-notif__dot" aria-hidden="true" />
            <span v-else class="yt-notif__dot yt-notif__dot--empty" aria-hidden="true" />
            <span
              class="yt-notif__logo"
              :style="item.logo ? { backgroundImage: `url(${item.logo})` } : undefined"
            >
              <i
                v-if="!item.logo"
                class="fa-solid"
                :class="item.eventType && String(item.eventType).includes('POINT') ? 'fa-coins' : 'fa-bell'"
              />
            </span>
            <span class="yt-notif__body">
              <span class="yt-notif__message">
                <strong>{{ item.title }}</strong>
                {{ item.content }}
              </span>
              <span class="yt-notif__meta">
                {{ item.time }}
                <span v-if="item.eventType" class="yt-notif__meta-sep">•</span>
                {{ item.eventType }}
              </span>
            </span>
          </button>
          <p v-if="!filteredNotifications.length" class="yt-notif__empty">
            {{ $t('header.notifEmpty') }}
          </p>
        </div>
      </template>
    </div>
  </a-drawer>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useInboxNotifications, type InboxItem } from '@/composables/useInboxNotifications'

type NotifFilter = 'all' | 'unread' | 'read'

const props = withDefaults(
  defineProps<{
    isLogin?: boolean
  }>(),
  { isLogin: false }
)

const emit = defineEmits<{
  (e: 'require-login'): void
}>()

const open = defineModel<boolean>('open', { default: false })
const hasUnread = defineModel<boolean>('hasUnread', { default: false })

const { t } = useI18n()
const { items, unreadCount, fetchInbox, markRead } = useInboxNotifications()

const drawerWidth = 400
const notifFilter = ref<NotifFilter>('all')

const notifTabs = computed(() => [
  { key: 'all' as NotifFilter, label: t('header.notifAll') },
  { key: 'unread' as NotifFilter, label: t('header.notifUnread') },
  { key: 'read' as NotifFilter, label: t('header.notifRead') },
])

const filteredNotifications = computed(() => {
  if (notifFilter.value === 'unread') {
    return items.value.filter((n) => !n.read)
  }
  if (notifFilter.value === 'read') {
    return items.value.filter((n) => n.read)
  }
  return items.value
})

const close = () => {
  open.value = false
}

const goLogin = () => {
  close()
  emit('require-login')
}

const markNotifRead = (item: InboxItem) => {
  if (!props.isLogin) return
  void markRead(item)
}

watch(
  () => props.isLogin,
  (ok) => {
    if (ok) void fetchInbox()
    else {
      items.value = []
      hasUnread.value = false
    }
  },
  { immediate: true }
)

watch(open, (visible) => {
  if (visible && props.isLogin) void fetchInbox()
})

watch(
  unreadCount,
  (n) => {
    hasUnread.value = props.isLogin && n > 0
  },
  { immediate: true }
)
</script>

<style scoped>
.yt-notif {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #fff;
  color: #222;
}

.yt-notif__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 20px 20px 12px;
}

.yt-notif__title-wrap {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  min-width: 0;
}

.yt-notif__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: #e8f1ff;
  color: #2f6fed;
  font-size: 18px;
  flex: 0 0 auto;
}

.yt-notif__title {
  margin: 0;
  font-size: 22px;
  font-weight: 700;
  line-height: 1.25;
  color: #111;
}

.yt-notif__subtitle {
  margin: 2px 0 0;
  font-size: 13px;
  color: #8a8a8a;
}

.yt-notif__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #666;
  font-size: 18px;
  cursor: pointer;
  flex: 0 0 auto;
}

.yt-notif__close:hover {
  background: #f2f2f2;
  color: #111;
}

.yt-notif__tabs {
  display: flex;
  gap: 8px;
  padding: 8px 20px 12px;
}

.yt-notif__tab {
  height: 34px;
  padding: 0 14px;
  border: 1px solid transparent;
  border-radius: 8px;
  background: #f3f3f3;
  color: #333;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
}

.yt-notif__tab.is-active {
  background: #eaf2ff;
  border-color: #2f6fed;
  color: #1a56db;
}

.yt-notif__tab:hover:not(.is-active) {
  background: #ebebeb;
}

.yt-notif__list {
  flex: 1 1 auto;
  overflow: auto;
  padding: 4px 0 16px;
}

.yt-notif__item {
  display: grid;
  grid-template-columns: 10px 44px 1fr;
  gap: 10px;
  align-items: flex-start;
  width: 100%;
  padding: 14px 20px;
  border: none;
  background: transparent;
  text-align: left;
  cursor: pointer;
}

.yt-notif__item:hover {
  background: #f7f9fc;
}

.yt-notif__dot {
  width: 8px;
  height: 8px;
  margin-top: 16px;
  border-radius: 50%;
  background: #2f6fed;
}

.yt-notif__dot--empty {
  background: transparent;
}

.yt-notif__logo {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: #f0f2f5 center / cover no-repeat;
  color: #888;
  font-size: 16px;
  overflow: hidden;
  flex: 0 0 auto;
}

.yt-notif__body {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.yt-notif__message {
  font-size: 14px;
  line-height: 1.45;
  color: #333;
}

.yt-notif__message strong {
  color: #111;
  font-weight: 700;
}

.yt-notif__meta {
  font-size: 12px;
  color: #8a8a8a;
}

.yt-notif__meta-sep {
  margin: 0 4px;
}

.yt-notif__login {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  flex: 1;
  padding: 48px 28px;
  text-align: center;
}

.yt-notif__login-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  margin-bottom: 4px;
  border-radius: 50%;
  background: #eef3ff;
  color: #2f6fed;
  font-size: 26px;
}

.yt-notif__login h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: #111;
}

.yt-notif__login p {
  margin: 0 0 8px;
  max-width: 280px;
  font-size: 14px;
  line-height: 1.5;
  color: #666;
}

.yt-notif__login-btn {
  height: 40px;
  padding: 0 22px;
  border: none;
  border-radius: 20px;
  background: #0f3d5e;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.yt-notif__login-btn:hover {
  background: #164e75;
}

.yt-notif__empty {
  margin: 24px 20px;
  font-size: 14px;
  color: #888;
  text-align: center;
}
</style>

<style>
.yt-notif-drawer-root .ant-drawer-content-wrapper {
  max-width: 100vw;
}

.yt-notif-drawer-root .ant-drawer-content {
  background: #fff !important;
}

.yt-notif-drawer-root .ant-drawer-header {
  display: none;
}

.yt-notif-drawer-root .ant-drawer-body {
  padding: 0 !important;
  height: 100%;
}

.yt-notif-drawer-root .ant-drawer-mask {
  background: rgba(0, 0, 0, 0.35) !important;
}
</style>
